/* tslint:disable:react-hooks-nesting */
import {type ComponentType, Suspense, useEffect, useState} from 'react';

import {GoogleReCaptchaProvider} from '@google-recaptcha/react';
import {message} from 'antd';
import {signOut} from 'aws-amplify/auth';
import qs from 'query-string';
import styled from 'styled-components';

import {Spinner} from '../Spinner';

import {ForgotPassword} from './ForgotPassword';
import {ForgotPasswordSubmit} from './ForgotPasswordSubmit';
import {PasswordExpiryWarning} from './PasswordExpiryWarning';
import {RequireNewPassword} from './RequireNewPassword';
import {fetchCurrentUser} from './utils';

import {ConfirmSignIn} from 'components/Auth/ConfirmSignIn';
import {SignInForm} from 'components/Auth/SignInForm';
import {GlobalStyle} from 'global.styles';
import bg from 'images/bg.svg';
import {UserContext} from 'lib/userContext';
import {config} from 'stage.config';
import type {ILazyComponent} from 'lib/utils';
import type {IAuthProps, IAuthQueryParams, IAuthState, IUser} from 'types';
import {AuthState} from 'types';

const Container = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 50px 20px 100px;
  height: 100%;
  background: url(${bg}) no-repeat 0 0;
  background-size: 100%;
`;

const Content = styled.div`
  width: 450px;
  margin: 0 auto 50px;
  .ant-input-prefix {
    color: rgba(0, 0, 0, 0.25) !important;
  }
  .ant-card {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  }
  .ant-card-head {
    padding: 0 40px;
    @media (max-width: 600px) {
      padding: 0 25px;
    }
  }
  .ant-card-body {
    padding: 40px 40px;
    @media (max-width: 600px) {
      padding: 25px 25px;
    }
  }
`;

const Logo = styled.img`
  width: 200px;
  height: 44px;
  margin-bottom: 30px;
  flex-shrink: 0;
`;

const initialState: IAuthState = {
  authState: AuthState.init
};

const allowedActions = [AuthState.signIn, AuthState.forgotPassword];

export const withAuthenticator = (Cmp: ILazyComponent<ComponentType<Record<string, unknown>>>) => {
  return () => {
    const [state, setState] = useState<IAuthState>(initialState);

    const setAuthState = (state: IAuthState) => {
      if (state.authState === AuthState.signedIn) {
        // Preserve query parameters except auth-related ones
        const urlParams = new URLSearchParams(window.location.search);
        // Remove auth form state query params
        const authParams = ['email', 'password', 'code', 'action', 'ref'];
        authParams.forEach((param) => {
          urlParams.delete(param);
        });
        // Construct the new URL preserving other query params like UTM tags
        const queryString = urlParams.toString();
        const newPath = window.location.pathname + (queryString ? `?${queryString}` : '');
        window.history.pushState('', '', newPath);
      }
      setState(state);
    };

    const handleSignOut = async (state: IAuthState = {authState: AuthState.signIn}) => {
      await signOut();
      setState(state);
      window.history.pushState('', '', '/');
    };

    useEffect(() => {
      const isValidAuthState = (action: string | undefined): action is AuthState => {
        return action !== undefined && allowedActions.includes(action as AuthState);
      };

      const directAuthState = ({email, code, action}: IAuthQueryParams) => {
        if (isValidAuthState(action)) {
          setAuthState({authState: action, email, code});
        }
      };

      const checkAuth = async () => {
        try {
          const currentUser = await fetchCurrentUser(true);
          if (currentUser) {
            setAuthState({authState: AuthState.signedIn, currentUser});
          } else {
            setAuthState({authState: AuthState.signIn});
          }
        } catch {
          setAuthState({authState: AuthState.signIn});
        }
      };

      const {email, password, code, action} = qs.parse(window.location.search) as IAuthQueryParams;

      if (isValidAuthState(action)) {
        if (action === AuthState.signIn && email && password) {
          // Security: Do not auto-sign in with credentials from URL query params
          // Instead, just set the auth state and show a warning
          message.warning(
            'Credentials in URL are being ignored for security reasons. Please sign in using the form.'
          );
          directAuthState({email, code, action});
        } else {
          directAuthState({email, code, action});
        }
      } else {
        void checkAuth();
      }
      void Cmp.preload();
    }, []);

    const renderAuthView = () => {
      const props: IAuthProps = {...state, setState: setAuthState};
      switch (state.authState) {
        case AuthState.confirmSignIn:
          return <ConfirmSignIn {...props} />;
        case AuthState.forgotPassword:
          return <ForgotPassword {...props} />;
        case AuthState.forgotPasswordSubmit:
          return <ForgotPasswordSubmit {...props} />;
        case AuthState.requireNewPassword:
          return <RequireNewPassword {...props} />;
        case AuthState.passwordExpiryWarning:
          return <PasswordExpiryWarning {...props} />;
        default:
          return <SignInForm {...props} />;
      }
    };

    const getAccessLevel = (currentUser: IUser) => {
      // Access level is now in the accessLevels array
      return (currentUser as {accessLevels?: string[]}).accessLevels?.[0] || '';
    };

    if (state.authState === AuthState.init) return <Spinner />;
    if (state.authState === AuthState.signedIn) {
      const currentUser = state.currentUser;
      if (!currentUser) {
        return <Spinner />;
      }
      return (
        <>
          <GlobalStyle />
          <Suspense fallback={<Spinner />}>
            <UserContext.Provider
              value={{
                onSignOut: (state) => {
                  void handleSignOut(state);
                },
                setCurrentUser: (user) => {
                  setState((state) => ({...state, currentUser: user}));
                },
                userEmail: currentUser.attributes?.email || '',
                userName: currentUser.attributes?.name || '',
                userPhone: currentUser.attributes?.phone_number || '',
                userId: currentUser.attributes?.sub || '',
                termsOfServiceUrl: currentUser.attributes?.['custom:tos_doc'] || '',
                termsOfServiceAcceptedAt: currentUser.attributes?.['custom:tos_accepted_at'] || '',
                accessLevel: getAccessLevel(currentUser),
                identityId: (currentUser as {identityId?: string | null}).identityId || null
              }}>
              <Cmp />
            </UserContext.Provider>
          </Suspense>
        </>
      );
    }
    return (
      <GoogleReCaptchaProvider type="v3" siteKey={config.recaptchaSiteKey} isEnterprise>
        <GlobalStyle />
        <Container>
          <Logo src="https://assets.monei.com/images/logo.svg" />
          <h2>Admin Dashboard</h2>
          <Content>{renderAuthView()}</Content>
        </Container>
      </GoogleReCaptchaProvider>
    );
  };
};
